import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, ExternalLink, Download } from 'lucide-react';
import { paymentService, type Payment } from '@/services/payment/payment.service';
import { format } from 'date-fns';
import { SubscriptionStatusBadge } from '@/components/billing/subscription-status-badge';
import { toast } from 'sonner';
import { useCompanyStore } from '@/store/use-company-store';

export default function InvoiceDetailPage() {
  const { invoiceNumber } = useParams<{ invoiceNumber: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentCompanyId } = useCompanyStore();
  const [payment, setPayment] = useState<Payment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!invoiceNumber || !currentCompanyId) return;

    const fetchPayment = async () => {
      try {
        setLoading(true);
        // Fetch payment history for current company and find the matching invoice
        const response = await paymentService.getPaymentHistory({ limit: 100 }) as { data?: { data?: Payment[] } | Payment[] } | Payment[];
        const payments: Payment[] = Array.isArray(response) 
          ? response 
          : (response.data && 'data' in response.data 
              ? response.data.data || [] 
              : Array.isArray(response.data) 
                ? response.data 
                : []);
        const foundPayment = payments.find((p: Payment) => p.invoiceNumber === invoiceNumber);
        
        if (foundPayment) {
          setPayment(foundPayment);
        } else {
          setError(t('billing.invoice.notFound'));
        }
      } catch (err: any) {
        const errorMessage = err instanceof Error ? err.message : t('billing.invoice.loadError');
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchPayment();
  }, [invoiceNumber, currentCompanyId, t]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-64" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !payment) {
    return (
      <div className="space-y-6">
        <Button variant="outline" onClick={() => navigate('/billing')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('billing.backToBilling')}
        </Button>
        <Card>
          <CardContent className="pt-6">
            <p className="text-rose-600">{error || t('billing.invoice.notFound')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusColor =
    payment.status === 'SUCCESSFUL'
      ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
      : payment.status === 'FAILED'
        ? 'bg-rose-100 text-rose-700 border-rose-200'
        : 'bg-slate-100 text-slate-700 border-slate-200';

  return (
    <div className="space-y-6">
      <Button variant="outline" onClick={() => navigate('/billing')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('billing.backToBilling')}
      </Button>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>{t('billing.invoice.title')}</CardTitle>
              <CardDescription className="mt-1">
                {t('billing.invoice.invoiceNumber')}: {payment.invoiceNumber}
              </CardDescription>
            </div>
            <Badge className={statusColor}>
              {t(`billing.table.status.${payment.status.toLowerCase()}`)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Payment Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-800">{t('billing.invoice.paymentInfo')}</h2>
              <dl className="space-y-2">
                <div>
                  <dt className="text-sm font-medium text-slate-600">{t('billing.invoice.invoiceNumber')}</dt>
                  <dd className="text-sm text-slate-900 font-mono">{payment.invoiceNumber || '—'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-600">{t('billing.invoice.transactionId')}</dt>
                  <dd className="text-sm text-slate-900 font-mono">{payment.transactionId || '—'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-600">{t('billing.invoice.plan')}</dt>
                  <dd className="text-sm text-slate-900">
                    {payment.subscription?.plan?.displayName || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-slate-600">{t('billing.invoice.amount')}</dt>
                  <dd className="text-sm text-slate-900 font-semibold">
                    ${payment.amountInCents} {payment.currency}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-800">{t('billing.invoice.timestamps')}</h2>
              <dl className="space-y-2">
                <div>
                  <dt className="text-sm font-medium text-slate-600">{t('billing.invoice.createdAt')}</dt>
                  <dd className="text-sm text-slate-900">
                    {payment.createdAt ? format(new Date(payment.createdAt), 'PPpp') : '—'}
                  </dd>
                </div>
                {payment.paidAt && (
                  <div>
                    <dt className="text-sm font-medium text-slate-600">{t('billing.invoice.paidAt')}</dt>
                    <dd className="text-sm text-slate-900">
                      {format(new Date(payment.paidAt), 'PPpp')}
                    </dd>
                  </div>
                )}
                {payment.expiresAt && (
                  <div>
                    <dt className="text-sm font-medium text-slate-600">{t('billing.invoice.expiresAt')}</dt>
                    <dd className="text-sm text-slate-900">
                      {format(new Date(payment.expiresAt), 'PPpp')}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Actions */}
          <div className="border-t pt-6 flex gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/billing')}
            >
              {t('billing.backToBilling')}
            </Button>
            {payment.invoicePdfUrl && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const response = await paymentService.getInvoicePresignedUrl(payment.id) as { data?: { presignedUrl?: string }; presignedUrl?: string };
                    const presignedUrl = response.data?.presignedUrl || response.presignedUrl;
                    if (presignedUrl) {
                      window.open(presignedUrl, '_blank', 'noopener,noreferrer');
                    } else {
                      toast.error(t('billing.invoice.downloadError'));
                    }
                  } catch (error: any) {
                    toast.error(error?.message || t('billing.invoice.downloadError'));
                  }
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                {t('billing.invoice.downloadInvoice')}
              </Button>
            )}
            {payment.receiptPdfUrl && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const response = await paymentService.getReceiptPresignedUrl(payment.id) as { data?: { presignedUrl?: string }; presignedUrl?: string };
                    const presignedUrl = response.data?.presignedUrl || response.presignedUrl;
                    if (presignedUrl) {
                      window.open(presignedUrl, '_blank', 'noopener,noreferrer');
                    } else {
                      toast.error(t('billing.invoice.downloadError'));
                    }
                  } catch (error: any) {
                    toast.error(error?.message || t('billing.invoice.downloadError'));
                  }
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                {t('billing.invoice.downloadReceipt')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

